/**
 * useFinanceTemplates hooks — Issue #368.
 *
 * 通过 @jinzhuan/api-client 调用财务模板底座接口；禁止前端伪状态。
 */
import { useCallback, useState } from 'react';
import { createApiClient, ApiError as ApiClientError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type FinanceTemplateSummaryDto =
  ApiComponents['schemas']['FinanceTemplateSummaryDto'];
export type FinanceTemplateDetailDto =
  ApiComponents['schemas']['FinanceTemplateDetailDto'];
export type FinanceTemplateFieldDto =
  ApiComponents['schemas']['FinanceTemplateFieldDto'];
export type FinanceTemplateListResponseDto =
  ApiComponents['schemas']['FinanceTemplateListResponseDto'];
export type FinanceTemplateRenderDto =
  ApiComponents['schemas']['FinanceTemplateRenderDto'];
export type FinanceTemplateRenderListResponseDto =
  ApiComponents['schemas']['FinanceTemplateRenderListResponseDto'];
export type RecordRenderDto = ApiComponents['schemas']['RecordRenderDto'];
export type SyncBuiltInResponseDto =
  ApiComponents['schemas']['SyncBuiltInResponseDto'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown, status?: number): ApiError {
  const effective =
    status ?? (err instanceof ApiClientError ? err.status : undefined);
  if (effective === 401) {
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED' };
  }
  if (effective === 403) {
    return { message: '没有权限执行该操作', code: 'FORBIDDEN' };
  }
  if (effective === 409) {
    return { message: '数据已被其他用户修改，请刷新后重试', code: 'CONFLICT' };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

export interface ListFinanceTemplatesFilter {
  category?: string;
  status?: string;
  templateCode?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export function useFinanceTemplates() {
  const [data, setData] = useState<FinanceTemplateListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(async (filter?: ListFinanceTemplatesFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {};
      if (filter?.category) query.category = filter.category;
      if (filter?.status) query.status = filter.status;
      if (filter?.templateCode) query.templateCode = filter.templateCode;
      if (filter?.search) query.search = filter.search;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data: ok, error: apiErr, response } = await apiClient.GET(
        '/api/v1/finance/templates',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setData(ok ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchList };
}

export function useFinanceTemplateDetail() {
  const [data, setData] = useState<FinanceTemplateDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchDetail = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data: ok, error: apiErr, response } = await apiClient.GET(
        '/api/v1/finance/templates/{id}',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        setData(null);
        return;
      }
      setData(ok ?? null);
    } catch (e) {
      setError(toApiError(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => setData(null), []);

  return { data, loading, error, fetchDetail, clear };
}

export function useFinanceTemplateActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const recordRender = useCallback(async (dto: RecordRenderDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/templates/renders',
        { body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const syncBuiltIn = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/templates/sync-built-in',
        { body: {} },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const publish = useCallback(
    async (id: string, expectedRecordVersion: number) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/templates/{id}/publish',
          {
            params: { path: { id } },
            body: { expectedRecordVersion },
          },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const deprecate = useCallback(
    async (id: string, expectedRecordVersion: number, reason: string) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/templates/{id}/deprecate',
          {
            params: { path: { id } },
            body: { expectedRecordVersion, reason },
          },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const archive = useCallback(
    async (id: string, expectedRecordVersion: number) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/templates/{id}/archive',
          {
            params: { path: { id } },
            body: { expectedRecordVersion },
          },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    error,
    recordRender,
    syncBuiltIn,
    publish,
    deprecate,
    archive,
  };
}
